/**
 * Compact source chip for agent evidence citations.
 *
 * Reads a free-form ``source`` string emitted by the agent (typically
 * ``doc:<UUID>`` or ``node:<elementId>``) and renders a small pill that:
 *   - For ``doc:`` UUIDs → looks up the filename via ``useDocumentName`` so the
 *     user sees ``DOCUMENT · invoice_2024.pdf`` instead of an opaque hash.
 *   - For ``doc:`` non-UUIDs (the LLM occasionally emits ``doc:<filename>``
 *     directly) → uses the verbatim payload as the display label so the chip
 *     doesn't degenerate to a useless 6-char suffix like ``72.PDF``.
 *   - For ``node:`` → shows ``ENTITY · <tail>`` (the trailing segment of the
 *     Neo4j element id).
 *   - For unparseable strings → renders the raw value verbatim in mono text.
 *
 * Used by both the Case Summary card's per-investigation evidence list and
 * the dedicated Case Summary page's investigation digest cards. Pure
 * display — not clickable. Use ``EvidenceCard`` (search/detail) when you
 * need the interactive open-in-overlay behaviour.
 */

import { FileText } from 'lucide-react'
import { documentMoment } from '@/features/source-snippets/reference'
import { useDocumentName } from '@/hooks/use-document-name'
import { cn } from '@/utils/cn'

export interface ParsedEvidenceSource {
  kind: 'doc' | 'entity' | 'unknown'
  /**
   * Set only when the payload is a real lookup key (UUID for ``doc:``, full
   * element id for ``node:``). ``null`` for ``doc:<filename>`` and unknown
   * shapes — callers should not attempt to fetch in that case.
   */
  id: string | null
  /** Display fallback when no resolved name is available. */
  tail: string
  raw: string
}

export function parseEvidenceSource(source: string): ParsedEvidenceSource {
  const raw = source.trim()
  if (!raw) return { kind: 'unknown', id: null, tail: '', raw: '' }
  if (raw.startsWith('doc:')) {
    // A document id, with or without a moment of a recording after it (`#t=<seconds>`); any
    // other payload is a label and nothing to look up.
    const moment = documentMoment(raw)
    if (moment) {
      return { kind: 'doc', id: moment.documentId, tail: moment.documentId.slice(-6), raw }
    }
    return { kind: 'doc', id: null, tail: raw.slice(4), raw }
  }
  if (raw.startsWith('node:')) {
    const id = raw.slice(5)
    const lastColon = id.lastIndexOf(':')
    const tail = lastColon >= 0 ? id.slice(lastColon + 1) : id.slice(-6) || id
    return { kind: 'entity', id, tail, raw }
  }
  return { kind: 'unknown', id: null, tail: raw.slice(-6) || raw, raw }
}

const FILENAME_CHIP_MAX = 32

function truncateForChip(value: string, maxLen: number = FILENAME_CHIP_MAX): string {
  return value.length > maxLen ? `${value.slice(0, maxLen - 1)}…` : value
}

export function EvidenceSourceChip({ source }: { source: string }) {
  const ref = parseEvidenceSource(source)
  const docNameQ = useDocumentName(ref.kind === 'doc' ? ref.id : null)
  const apiFilename = docNameQ.data ?? null
  const rawSuffix = ref.kind === 'doc' ? (apiFilename ?? ref.tail) : ref.tail
  const displaySuffix = ref.kind === 'doc' ? truncateForChip(rawSuffix) : rawSuffix
  const suffixTitle =
    ref.kind === 'doc' && rawSuffix.length > FILENAME_CHIP_MAX ? rawSuffix : undefined

  if (ref.kind === 'unknown') {
    return <span className="font-mono text-[10px] text-muted-foreground/80">{ref.raw}</span>
  }

  return (
    <span
      className={cn(
        'inline-flex w-fit max-md:max-w-full items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
        ref.kind === 'doc'
          ? 'border-info/30 bg-info/10 text-info'
          : 'border-primary/30 bg-primary/5 text-primary'
      )}
    >
      <FileText className="size-3 max-md:shrink-0" />
      {ref.kind === 'doc' ? 'Document' : 'Entity'}
      {displaySuffix && (
        <span
          className={cn(
            'font-mono text-[9px] text-muted-foreground/80 max-md:min-w-0 max-md:truncate',
            // Filenames look like shouting in uppercase — always render
            // doc suffixes in normal case. Entity ids stay uppercase.
            ref.kind === 'doc' && 'normal-case tracking-normal'
          )}
          title={suffixTitle}
        >
          ·{displaySuffix}
        </span>
      )}
    </span>
  )
}
